import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, ChapterHeading } from "./Shared";

export const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const steps = [
  {
    n: "01",
    title: "Deploy your touchpoints",
    body: "Place PulsePoint print media at entrances, gates and food courts. Turn your screens into PulseScreen sentiment touchpoints. We guide you through both during setup.",
  },
  {
    n: "02",
    title: "Your audience interacts",
    body: "Visitors engage in one tap — verified by location, anonymous by default. Each signal is a real person, physically present, right now.",
  },
  {
    n: "03",
    title: "Your Command Centre lights up",
    body: "Watch mood move by zone and hour. Catch urgency flags before they trend. Decide with data, not gut feel.",
  },
];

export function HowItWorks() {
  return (
    <section id="how" className="py-28 lg:py-36 bg-[#080808] border-y border-white/10" data-testid="section-how">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <ChapterHeading number="05" title="HOW IT WORKS" />
        <div className="grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {steps.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.12} className="bg-[#0a0a0a]">
              <div className="p-10 lg:p-12 h-full group hover:bg-[#101010] transition-colors duration-300">
                <p className="font-mono-tech text-6xl font-bold text-outline group-hover:text-[#E5FF00] group-hover:[-webkit-text-stroke:0px] transition-all duration-300">{s.n}</p>
                <h3 className="mt-8 font-display font-bold text-2xl">{s.title}</h3>
                <p className="mt-4 text-sm text-zinc-500 leading-relaxed">{s.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingCalculator({ onGetStarted }) {
  const [cfg, setCfg] = useState({ setup_fee: 9999, per_interaction: 4, weekly_per_channel: 2999 });
  const [interactions, setInteractions] = useState(5000);
  const [weeks, setWeeks] = useState(4);
  const [printQr, setPrintQr] = useState(true);
  const [kiosk, setKiosk] = useState(true);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const channels = (printQr ? 1 : 0) + (kiosk ? 1 : 0);
  const total = cfg.setup_fee + interactions * cfg.per_interaction + Math.max(channels, 1) * weeks * cfg.weekly_per_channel;

  const toggleCls = (on) =>
    `flex-1 border px-4 py-3 font-mono-tech text-[11px] tracking-[0.2em] transition-colors duration-200 ${
      on ? "border-[#E5FF00] text-[#E5FF00] bg-[#E5FF00]/5" : "border-white/15 text-zinc-500 hover:border-white/40"
    }`;

  return (
    <section id="pricing" className="py-28 lg:py-36" data-testid="section-pricing">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
        <FadeUp className="lg:col-span-5">
          <p className="font-mono-tech text-[11px] tracking-[0.3em] text-[#E5FF00] mb-6">/PRICING</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            PRICED ON REALITY, NOT SEATS.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            One-time setup. A few rupees per real audience signal. A flat weekly rate per touchpoint channel. Move the
            sliders — the quote moves with you.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="lg:col-span-7">
          <div className="border border-white/15 bg-[#0a0a0a] p-8 lg:p-12" data-testid="pricing-calculator">
            <div className="space-y-10">
              <div>
                <div className="flex justify-between font-mono-tech text-[11px] tracking-[0.2em] text-zinc-500 mb-4">
                  <span>EXPECTED INTERACTIONS</span>
                  <span className="text-white" data-testid="calc-interactions-value">{interactions.toLocaleString()}</span>
                </div>
                <input
                  type="range" min={1000} max={100000} step={500} value={interactions}
                  onChange={(e) => setInteractions(Number(e.target.value))}
                  className="w-full accent-[#E5FF00]"
                  data-testid="calc-interactions-slider"
                />
              </div>
              <div>
                <div className="flex justify-between font-mono-tech text-[11px] tracking-[0.2em] text-zinc-500 mb-4">
                  <span>ANALYSIS DURATION</span>
                  <span className="text-white" data-testid="calc-weeks-value">{weeks} WEEKS</span>
                </div>
                <input
                  type="range" min={1} max={52} value={weeks}
                  onChange={(e) => setWeeks(Number(e.target.value))}
                  className="w-full accent-[#E5FF00]"
                  data-testid="calc-weeks-slider"
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setPrintQr(!printQr)} className={toggleCls(printQr)} data-testid="calc-channel-print">
                  PULSEPOINTS — PRINT {printQr ? "✓" : ""}
                </button>
                <button type="button" onClick={() => setKiosk(!kiosk)} className={toggleCls(kiosk)} data-testid="calc-channel-kiosk">
                  PULSESCREENS — KIOSK {kiosk ? "✓" : ""}
                </button>
              </div>
              <div className="border-t border-white/10 pt-8 flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-500 mb-2">INDICATIVE QUOTE</p>
                  <p className="font-mono-tech font-bold text-5xl text-[#E5FF00]" data-testid="calc-total">
                    {inr(total)}
                  </p>
                  <p className="mt-2 text-[11px] text-zinc-500 max-w-xs leading-relaxed">
                    Indicative only — your final quote is confirmed during onboarding based on your exact venue setup.
                  </p>
                </div>
                <button
                  onClick={onGetStarted}
                  className="group flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide px-8 py-4 hover:bg-white transition-colors duration-200"
                  data-testid="calc-cta-start"
                >
                  LOCK THIS QUOTE
                  <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export function FinalCTA({ onGetStarted }) {
  return (
    <section className="py-32 lg:py-44 grid-lines relative overflow-hidden" data-testid="section-final-cta">
      <div className="spotlight absolute inset-0 pointer-events-none" />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 text-left relative">
        <FadeUp>
          <p className="font-mono-tech text-[11px] tracking-[0.3em] text-[#E5FF00] mb-8">READY WHEN YOU ARE</p>
          <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.95]">
            STOP GUESSING.<br />
            <span className="text-[#E5FF00]">START LISTENING.</span>
          </h2>
          <p className="mt-8 max-w-lg text-zinc-400 leading-relaxed">
            Two minutes to sign up. A guided setup for geofencing, PulsePoints and PulseScreens. Instant quote, online payment,
            and your Command Centre activates the same day.
          </p>
          <button
            onClick={onGetStarted}
            className="group mt-10 inline-flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide px-10 py-5 hover:bg-white transition-colors duration-200"
            data-testid="final-cta-start"
          >
            CREATE YOUR BUSINESS ACCOUNT
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </FadeUp>
      </div>
    </section>
  );
}
